"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { LogoMark } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { createClient } from "@/lib/supabase";
import type { UserRole } from "@/lib/auth";

export interface NavDropdownItem {
  href: string;
  label: string;
  description: string;
  badge?: string;
}

export const FIND_A_TRADE_ITEMS: NavDropdownItem[] = [
  { href: "/browse", label: "Browse Tradespeople", description: "Explore local tradespeople and profiles" },
  { href: "/find", label: "Find by Location", description: "Explore trades near any UK postcode" },
  { href: "/snap", label: "Snap and Fix", description: "Upload a photo, get an instant diagnosis", badge: "New" },
  { href: "/diagnose", label: "What's Wrong With My House?", description: "Guided diagnosis for common problems" },
  { href: "/map", label: "Live Map", description: "See what's happening near you right now" },
  { href: "/ask", label: "Ask a Tradesperson", description: "Get free advice from professionals", badge: "New" },
];

export const COSTS_AND_GUIDES_ITEMS: NavDropdownItem[] = [
  { href: "/cost-estimator", label: "Cost Estimator", description: "Get price ranges for any job" },
  { href: "/prices", label: "Trade Price Index", description: "Average costs across all trades" },
  { href: "/leaderboards", label: "Cost Leaderboards", description: "Cheapest and priciest areas in the UK" },
  { href: "/compare", label: "Compare Cities", description: "Side-by-side pricing between towns" },
  { href: "/problems", label: "Home Problem Guides", description: "267 common issues explained" },
  { href: "/permits", label: "Planning Permission", description: "Check if your project needs approval" },
  { href: "/guides", label: "Cost Guides", description: "Detailed breakdowns by trade" },
];

function NavDropdown({
  label,
  items,
  open,
  onToggle,
  onHoverEnter,
  onHoverLeave,
  pathname,
}: {
  label: string;
  items: NavDropdownItem[];
  open: boolean;
  onToggle: () => void;
  onHoverEnter: () => void;
  onHoverLeave: () => void;
  pathname: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        if (open) onToggle();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onToggle]);

  const isActive = items.some(
    (item) => pathname === item.href || pathname.startsWith(item.href + "/")
  );

  return (
    <div className="relative" ref={ref} onMouseEnter={onHoverEnter} onMouseLeave={onHoverLeave}>
      <button
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="true"
        className={`flex items-center gap-1 text-[13px] font-medium px-4 py-1.5 rounded-sm transition-all tracking-[0.2px] bg-transparent border-none cursor-pointer font-sans ${
          isActive
            ? "text-forest font-semibold bg-forest/[0.06]"
            : "text-text-body hover:text-forest hover:bg-forest/[0.04]"
        }`}
      >
        {label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        >
          <path
            d="M2.5 3.75L5 6.25L7.5 3.75"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div
        className={`absolute top-[calc(100%+8px)] left-0 bg-white dark:bg-slate-800 rounded-md shadow-[0_12px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.04)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.06)] p-2 min-w-[280px] transition-all duration-200 ${
          open
            ? "opacity-100 pointer-events-auto translate-y-0"
            : "opacity-0 pointer-events-none -translate-y-1"
        }`}
      >
        {items.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className="block px-3.5 py-2.5 no-underline rounded-sm transition-all hover:bg-warm-bg dark:hover:bg-slate-700 group"
            onClick={onToggle}
          >
            <span className="flex items-center gap-2">
              <span
                className={`text-[13px] font-medium ${
                  pathname === item.href || pathname.startsWith(item.href + "/")
                    ? "text-forest font-semibold"
                    : "text-text-body group-hover:text-forest"
                }`}
              >
                {item.label}
              </span>
              {item.badge && (
                <span className="text-[10px] font-bold uppercase tracking-wide text-forest bg-forest/10 px-1.5 py-0.5 rounded-sm">
                  {item.badge}
                </span>
              )}
            </span>
            <span className="text-[11.5px] text-text-muted mt-0.5 block">
              {item.description}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function MobileAccordion({
  label,
  items,
  open,
  onToggle,
  onNavigate,
  pathname,
}: {
  label: string;
  items: NavDropdownItem[];
  open: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  pathname: string;
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center justify-between w-full px-4 py-3.5 text-[15px] font-medium text-text-body bg-transparent border-none cursor-pointer font-sans rounded-sm active:bg-warm-bg"
      >
        {label}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        >
          <path
            d="M3.5 5.25L7 8.75L10.5 5.25"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="pl-4 pb-2">
          {items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`block px-4 py-3 no-underline rounded-sm active:bg-warm-bg ${
                pathname === item.href || pathname.startsWith(item.href + "/")
                  ? "text-forest font-semibold"
                  : "text-text-body"
              }`}
              onClick={onNavigate}
            >
              <span className="flex items-center gap-2">
                <span className="text-[14px] font-medium">{item.label}</span>
                {item.badge && (
                  <span className="text-[9px] font-bold uppercase tracking-wide text-forest bg-forest/10 px-1.5 py-0.5 rounded-sm">
                    {item.badge}
                  </span>
                )}
              </span>
              <span className="text-[12px] text-text-muted mt-0.5 block">
                {item.description}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Navbar({
  userRole,
  unreadCount = 0,
  userName,
  userPhotoUrl,
}: {
  userRole?: UserRole;
  unreadCount?: number;
  userName?: string;
  userPhotoUrl?: string;
}) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const hoverCloseRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const isLoggedIn = userRole === "tradesperson" || userRole === "homeowner";

  function navLinkClass(href: string) {
    const active = pathname === href || pathname.startsWith(href + "/");
    return `text-[13px] font-medium px-4 py-1.5 rounded-sm transition-all tracking-[0.2px] no-underline ${
      active
        ? "text-forest font-semibold bg-forest/[0.06]"
        : "text-text-body hover:text-forest hover:bg-forest/[0.04]"
    }`;
  }

  function mobileNavLinkClass(href: string) {
    const active = pathname === href || pathname.startsWith(href + "/");
    return `block px-4 py-3.5 text-[15px] font-medium no-underline rounded-sm active:bg-warm-bg ${
      active ? "text-forest font-semibold" : "text-text-body"
    }`;
  }

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 10);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        profileRef.current &&
        !profileRef.current.contains(e.target as Node)
      ) {
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    function handleEscape(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") setMobileOpen(false);
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [mobileOpen]);

  const [previousPathname, setPreviousPathname] = useState(pathname);
  if (previousPathname !== pathname) {
    setPreviousPathname(pathname);
    setOpenDropdown(null);
    setMobileAccordion(null);
  }

  async function handleLogout() {
    const supabase = createClient();
    await supabase.auth.signOut();
    // Logout needs a fresh document to clear cached auth UI (see AGENTS.md).
    window.location.href = new URL("/", window.location.origin).href;
  }

  function toggleDropdown(name: string) {
    setOpenDropdown(openDropdown === name ? null : name);
  }

  function handleDropdownEnter(name: string) {
    clearTimeout(hoverCloseRef.current);
    setOpenDropdown(name);
  }

  function handleDropdownLeave() {
    hoverCloseRef.current = setTimeout(() => setOpenDropdown(null), 150);
  }

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[1000] flex items-center justify-between px-5 md:px-10 h-16 bg-warm-bg/92 dark:bg-[#0f172a]/95 backdrop-blur-[20px] backdrop-saturate-[1.4] border-b border-forest/6 dark:border-white/[0.06] transition-shadow duration-300 ${
          scrolled
            ? "shadow-[0_2px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_24px_rgba(0,0,0,0.3)]"
            : ""
        }`}
      >
        {/* Brand */}
        <Link
          href="/"
          onClick={() => setMobileOpen(false)}
          className="flex items-center gap-2.5 no-underline shrink-0"
        >
          <LogoMark className="text-forest" />
          <span className="font-serif text-[24px] font-semibold text-forest tracking-[-0.3px]">
            Tradesfolk
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1.5">
          {!isLoggedIn && (
            <>
              <NavDropdown
                label="Find a Trade"
                items={FIND_A_TRADE_ITEMS}
                open={openDropdown === "find"}
                onToggle={() => toggleDropdown("find")}
                onHoverEnter={() => handleDropdownEnter("find")}
                onHoverLeave={handleDropdownLeave}
                pathname={pathname}
              />
              <NavDropdown
                label="Costs and Guides"
                items={COSTS_AND_GUIDES_ITEMS}
                open={openDropdown === "costs"}
                onToggle={() => toggleDropdown("costs")}
                onHoverEnter={() => handleDropdownEnter("costs")}
                onHoverLeave={handleDropdownLeave}
                pathname={pathname}
              />
              <Link href="/how-it-works" className={navLinkClass("/how-it-works")}>
                How It Works
              </Link>
              <Link href="/for-tradespeople" className={navLinkClass("/for-tradespeople")}>
                For Tradespeople
              </Link>
              <ThemeToggle />
              <Link
                href="/login"
                className="text-sm font-medium text-forest px-3 py-2 ml-1 no-underline transition-colors hover:text-copper"
              >
                Log In
              </Link>
              <Link
                href="/signup/tradesperson"
                className="text-sm font-semibold text-white bg-forest px-4 py-2 ml-1 rounded-sm no-underline transition-all hover:bg-forest-light"
              >
                List for Free
              </Link>
            </>
          )}

          {userRole === "homeowner" && (
            <>
              <NavDropdown
                label="Find a Trade"
                items={FIND_A_TRADE_ITEMS}
                open={openDropdown === "find"}
                onToggle={() => toggleDropdown("find")}
                onHoverEnter={() => handleDropdownEnter("find")}
                onHoverLeave={handleDropdownLeave}
                pathname={pathname}
              />
              <NavDropdown
                label="Costs and Guides"
                items={COSTS_AND_GUIDES_ITEMS}
                open={openDropdown === "costs"}
                onToggle={() => toggleDropdown("costs")}
                onHoverEnter={() => handleDropdownEnter("costs")}
                onHoverLeave={handleDropdownLeave}
                pathname={pathname}
              />
              <Link
                href="/post-job"
                className={navLinkClass("/post-job")}
              >
                Post a Job
              </Link>
              <Link
                href="/homeowner/saved"
                className="relative p-2 rounded-sm transition-all hover:bg-forest/[0.04] no-underline inline-flex items-center"
                aria-label="Saved tradespeople"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-text-body"
                >
                  <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </Link>
            </>
          )}

          {userRole === "tradesperson" && (
            <Link href="/jobs" className={navLinkClass("/jobs")}>
              Jobs Near You
            </Link>
          )}

          {isLoggedIn && (
            <Link
              href={
                userRole === "tradesperson"
                  ? "/dashboard/messages"
                  : "/homeowner/messages"
              }
              className="relative p-2 rounded-sm transition-all hover:bg-forest/[0.04] no-underline inline-flex items-center"
              aria-label="Messages"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-text-body"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-[16px] h-[16px] bg-copper text-white rounded-full text-[9px] font-bold flex items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Link>
          )}

          {isLoggedIn && <ThemeToggle />}

          {isLoggedIn && (
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                aria-expanded={profileOpen}
                aria-haspopup="true"
                className="flex items-center gap-2 text-[13px] font-medium text-text-body px-3.5 py-2 rounded-sm transition-all hover:text-forest hover:bg-forest/[0.04] tracking-[0.2px] bg-transparent border-none cursor-pointer font-sans"
              >
                {userPhotoUrl ? (
                  <Image
                    src={userPhotoUrl}
                    alt={userName ?? "Account"}
                    width={24}
                    height={24}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                ) : (
                  <span className="w-6 h-6 rounded-full bg-forest text-white text-[10px] font-bold flex items-center justify-center">
                    {userName?.charAt(0).toUpperCase() ?? "?"}
                  </span>
                )}
                {userName ?? "Account"}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  className={`transition-transform duration-200 ${profileOpen ? "rotate-180" : ""}`}
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div
                className={`absolute top-[calc(100%+8px)] right-0 bg-white dark:bg-slate-800 rounded-md shadow-[0_12px_40px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.04)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.06)] p-2 min-w-[180px] transition-all duration-200 ${
                  profileOpen
                    ? "opacity-100 pointer-events-auto translate-y-0"
                    : "opacity-0 pointer-events-none -translate-y-1"
                }`}
              >
                {userRole === "tradesperson" && (
                  <>
                    <Link
                      href="/dashboard"
                      className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg dark:hover:bg-slate-700 hover:text-forest"
                      onClick={() => setProfileOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      href="/dashboard/profile"
                      className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg dark:hover:bg-slate-700 hover:text-forest"
                      onClick={() => setProfileOpen(false)}
                    >
                      Edit Profile
                    </Link>
                    <Link
                      href="/dashboard/messages"
                      className="flex items-center px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-forest"
                      onClick={() => setProfileOpen(false)}
                    >
                      Messages
                      {unreadCount > 0 && (
                        <span className="ml-1.5 w-[16px] h-[16px] bg-copper text-white rounded-full text-[9px] font-bold inline-flex items-center justify-center">
                          {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                      )}
                    </Link>
                    <Link
                      href="/refer"
                      className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg dark:hover:bg-slate-700 hover:text-forest"
                      onClick={() => setProfileOpen(false)}
                    >
                      Refer a Tradesperson
                    </Link>
                  </>
                )}
                {userRole === "homeowner" && (
                  <>
                    <Link
                      href="/homeowner/dashboard"
                      className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg dark:hover:bg-slate-700 hover:text-forest"
                      onClick={() => setProfileOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      href="/homeowner/saved"
                      className="block px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg dark:hover:bg-slate-700 hover:text-forest"
                      onClick={() => setProfileOpen(false)}
                    >
                      Saved Tradespeople
                    </Link>
                    <Link
                      href="/homeowner/messages"
                      className="flex items-center px-3.5 py-2.5 text-[13px] font-medium text-text-body no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-forest"
                      onClick={() => setProfileOpen(false)}
                    >
                      Messages
                      {unreadCount > 0 && (
                        <span className="ml-1.5 w-[16px] h-[16px] bg-copper text-white rounded-full text-[9px] font-bold inline-flex items-center justify-center">
                          {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                      )}
                    </Link>
                  </>
                )}
                <div className="h-px bg-border-light mx-1 my-1.5" />
                <button
                  onClick={() => {
                    setProfileOpen(false);
                    handleLogout();
                  }}
                  className="block w-full text-left px-3.5 py-2.5 text-[13px] font-medium text-text-muted no-underline rounded-sm transition-all hover:bg-warm-bg hover:text-error bg-transparent border-none cursor-pointer font-sans"
                >
                  Log Out
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Theme + Mobile Toggle */}
        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="flex items-center justify-center w-11 h-11 rounded-sm bg-transparent border-none cursor-pointer transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.08] text-charcoal"
            aria-label="Menu"
          >
            {mobileOpen ? (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <line x1="4" y1="4" x2="16" y2="16" />
                <line x1="16" y1="4" x2="4" y2="16" />
              </svg>
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              >
                <line x1="3" y1="5" x2="17" y2="5" />
                <line x1="3" y1="10" x2="17" y2="10" />
                <line x1="3" y1="15" x2="17" y2="15" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Nav Panel */}
      <div
        className={`fixed top-16 left-0 right-0 bottom-0 bg-white dark:bg-[#0f172a] z-[999] overflow-y-auto px-4 pb-8 pt-2 transition-all duration-250 flex flex-col md:hidden ${
          mobileOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
      >
        {!isLoggedIn && (
          <>
            <MobileAccordion
              label="Find a Trade"
              items={FIND_A_TRADE_ITEMS}
              open={mobileAccordion === "find"}
              onToggle={() =>
                setMobileAccordion(mobileAccordion === "find" ? null : "find")
              }
              onNavigate={() => setMobileOpen(false)}
              pathname={pathname}
            />
            <MobileAccordion
              label="Costs and Guides"
              items={COSTS_AND_GUIDES_ITEMS}
              open={mobileAccordion === "costs"}
              onToggle={() =>
                setMobileAccordion(mobileAccordion === "costs" ? null : "costs")
              }
              onNavigate={() => setMobileOpen(false)}
              pathname={pathname}
            />
            <Link
              href="/how-it-works"
              className={mobileNavLinkClass("/how-it-works")}
              onClick={() => setMobileOpen(false)}
            >
              How It Works
            </Link>
            <Link
              href="/for-tradespeople"
              className={mobileNavLinkClass("/for-tradespeople")}
              onClick={() => setMobileOpen(false)}
            >
              For Tradespeople
            </Link>
          </>
        )}

        {userRole === "homeowner" && (
          <>
            <MobileAccordion
              label="Find a Trade"
              items={FIND_A_TRADE_ITEMS}
              open={mobileAccordion === "find"}
              onToggle={() =>
                setMobileAccordion(mobileAccordion === "find" ? null : "find")
              }
              onNavigate={() => setMobileOpen(false)}
              pathname={pathname}
            />
            <MobileAccordion
              label="Costs and Guides"
              items={COSTS_AND_GUIDES_ITEMS}
              open={mobileAccordion === "costs"}
              onToggle={() =>
                setMobileAccordion(mobileAccordion === "costs" ? null : "costs")
              }
              onNavigate={() => setMobileOpen(false)}
              pathname={pathname}
            />
            <Link
              href="/post-job"
              className={mobileNavLinkClass("/post-job")}
              onClick={() => setMobileOpen(false)}
            >
              Post a Job
            </Link>
            <div className="h-px bg-border-light mx-4 my-1" />
            <Link
              href="/homeowner/dashboard"
              className={mobileNavLinkClass("/homeowner/dashboard")}
              onClick={() => setMobileOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              href="/homeowner/saved"
              className={mobileNavLinkClass("/homeowner/saved")}
              onClick={() => setMobileOpen(false)}
            >
              Saved
            </Link>
            <Link
              href="/homeowner/messages"
              className={mobileNavLinkClass("/homeowner/messages")}
              onClick={() => setMobileOpen(false)}
            >
              Messages
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex w-5 h-5 bg-copper text-white rounded-full text-[10px] font-bold items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Link>
          </>
        )}

        {userRole === "tradesperson" && (
          <>
            <Link
              href="/jobs"
              className={mobileNavLinkClass("/jobs")}
              onClick={() => setMobileOpen(false)}
            >
              Jobs Near You
            </Link>
            <div className="h-px bg-border-light mx-4 my-1" />
            <Link
              href="/dashboard"
              className={mobileNavLinkClass("/dashboard")}
              onClick={() => setMobileOpen(false)}
            >
              Dashboard
            </Link>
            <Link
              href="/dashboard/profile"
              className={mobileNavLinkClass("/dashboard/profile")}
              onClick={() => setMobileOpen(false)}
            >
              Edit Profile
            </Link>
            <Link
              href="/dashboard/messages"
              className={mobileNavLinkClass("/dashboard/messages")}
              onClick={() => setMobileOpen(false)}
            >
              Messages
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex w-5 h-5 bg-copper text-white rounded-full text-[10px] font-bold items-center justify-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </Link>
            <Link
              href="/refer"
              className={mobileNavLinkClass("/refer")}
              onClick={() => setMobileOpen(false)}
            >
              Refer a Tradesperson
            </Link>
          </>
        )}

        <div className="h-px bg-border-light mx-4 my-2" />

        {!isLoggedIn && (
          <div className="flex flex-col gap-2.5 px-4 pt-4">
            <Link
              href="/signup/tradesperson"
              className="block text-center px-5 py-3.5 rounded-sm text-[15px] font-semibold bg-forest text-white no-underline font-sans"
              onClick={() => setMobileOpen(false)}
            >
              List for Free
            </Link>
            <Link
              href="/login"
              className="block text-center px-5 py-3.5 rounded-sm text-[15px] font-semibold border-[1.5px] border-forest/18 text-forest no-underline font-sans"
              onClick={() => setMobileOpen(false)}
            >
              Log In
            </Link>
          </div>
        )}

        {isLoggedIn && (
          <div className="flex flex-col gap-2.5 px-4 pt-4">
            <button
              onClick={() => {
                setMobileOpen(false);
                handleLogout();
              }}
              className="w-full text-center px-5 py-3.5 rounded-sm text-[15px] font-semibold border-[1.5px] border-border text-text-muted bg-transparent cursor-pointer font-sans"
            >
              Log Out
            </button>
          </div>
        )}
      </div>
    </>
  );
}
